<script>
  import { UserModal, InfoBox, ConfirmModal } from "../../components/index";
  import {
    Button,
    Icon,
    Text,
    Tag,
    Badge,
    Flex,
    FlexItem,
  } from "@gl/design-system";
  import {
    faPen,
    faInfoCircle,
    faRedo,
    faArrowRotateLeft,
  } from "@fortawesome/pro-regular-svg-icons";
  import { faInfoCircle as faInfoCircleSolid } from "@fortawesome/pro-solid-svg-icons";
  import { _, callout, user as userStore } from "../../stores/index";
  import { format } from "date-fns";
  import { createEventDispatcher, getContext, onMount } from "svelte";
  import { resetPassword } from "../../repositories/users";
  import ToggleSwitch from "./ToggleSwitch.svelte";
  import { UsersRepository } from "../../repositories";
  const dispatch = createEventDispatcher();
  const { isAuthorized } = getContext("isAuthorized");
  import {
    parseUrl,
    setModalParams,
    clearModalParams,
  } from "../../utils/index";
  import { ModalType } from "../../types/modal";

  export let user;
  const config = getContext("config");

  let modal;
  let confirmModal;

  let statusInfo;
  let connectionInfo;

  let statusMap = {
    blocked: "error",
    unlinked: "error",
    null: "warning",
    active: "success",
    expired: "error",
  };

  let loading = {};

  let deactivateModal;
  let deactivate = user.data?.blocked;

  async function deactivateUser(blocked) {
    try {
      await UsersRepository.editUser(user.id, { blocked: blocked });
    } finally {
      dispatch("change");
    }
  }

  function defineConnectionType() {
    if (!user.data.externalId) {
      user.connection = "pending";
      return null;
    }

    const splitExternalID = user.data.externalId.split("|");
    if (splitExternalID.length === 1) user.connection = "manual";
    if (splitExternalID.length === 2) user.connection = splitExternalID[0];
    if (splitExternalID.length > 2) user.connection = splitExternalID[1];
  }

  async function handleResetPassword(modal) {
    if (user.id === $userStore.data.id && !modal) {
      confirmModal = true;
    } else {
      loading.reset = true;
      try {
        await UsersRepository.resetPassword(user.id);
        callout.set({
          msg: $_("user.reset.password.success.callout"),
          status: "success",
          timeout: true,
        });
      } finally {
        loading.reset = false;
      }
    }
  }

  function openModal() {
    modal = true;
    setModalParams("userModal");
  }

  function closeModal() {
    modal = false;
    clearModalParams("userModal");
  }

  onMount(() => {
    defineConnectionType();
    const query = parseUrl(window.location.search);
    if (query.userModal) {
      openModal();
    }
  });
</script>

<svelte:head>
  <title>
    {$_("users.data")}: {`${user.data.firstName} ${user.data.lastName}`} | {$_(
      "common.users",
    )} | {$_("access-manager")}
  </title>
</svelte:head>

{#if modal}
  <UserModal
    bind:active={modal}
    on:close={() => {
      closeModal();
    }}
    on:success={(e) => dispatch("change", { userId: e.detail.userId })}
    data={{ id: user.id, ...user.data }}
    modalType={user.data?.isServiceAccount
      ? ModalType.ServiceAccountPut
      : ModalType.Put}
    groups={user.groups}
    roles={user.roles}
  />
{/if}

{#if confirmModal}
  <ConfirmModal
    bind:active={confirmModal}
    on:close={() => {
      confirmModal = false;
    }}
    handleYes={() => handleResetPassword(true)}
    title={$_("reset.password")}
    yes={$_("reset.password")}
    confirmStatus="warning"
  >
    <p>{$_("reset.self.password.warning")}</p>
  </ConfirmModal>
{/if}

{#if deactivateModal}
  <ConfirmModal
    bind:active={deactivateModal}
    on:close={() => {
      deactivateModal = false;
      deactivate = !deactivate;
    }}
    handleYes={() => deactivateUser(deactivate)}
    title={deactivate ? $_("user.deactivate") : $_("user.activate")}
    yes={deactivate ? $_("common.deactivate") : $_("common.activate")}
    confirmStatus={deactivate ? "error" : "primary"}
  >
    <div class="mb-0">
      {deactivate ? $_("user.deactivate.text") : $_("user.activate.text")}
    </div>
  </ConfirmModal>
{/if}

<Flex class="align-items-center">
  {#if $isAuthorized("user_write")}
    <FlexItem>
      <Button
        class="mt-3 mb-4 mr-3"
        outlined
        size="small"
        on:click={() => {
          openModal();
        }}
      >
        <Icon icon={faPen} />
        <Text>{$_("buttons.edit")}</Text>
      </Button>
    </FlexItem>
  {/if}

  {#if $isAuthorized("user_write") && config.OKTA_ISSUER && !user.data.isServiceAccount}
    <FlexItem>
      <Button
        class="mt-3 mb-4"
        outlined
        size="small"
        on:click={() => {
          handleResetPassword();
        }}
        loading={loading.reset}
      >
        <Icon icon={faArrowRotateLeft} />
        <Text>{$_("user.reset.password")}</Text>
      </Button>
    </FlexItem>
  {/if}

  {#if $isAuthorized("user_write")}
    <FlexItem pushEnd>
      <ToggleSwitch
        value={deactivate}
        on:change={(e) => {
          deactivateModal = true;
          deactivate = e.detail;
        }}
        label={user.data?.blocked ? $_("user.activate") : $_("user.deactivate")}
      />
    </FlexItem>
  {/if}
</Flex>

<div class="w-100 user-main-card">
  <div class="user-row" id="user-name">
    <div class="user-title">
      {$_("common.name")}
    </div>
    <div class="user-td">
      {user.data.firstName}
      {#if user.data.isServiceAccount}
        <Tag status="default" class="ml-2" style="font-size: 10px;">
          {$_("users.service_account")}
        </Tag>
      {:else}
        {user.data.lastName}
      {/if}
    </div>
  </div>

  <div class="user-row" id="user-email">
    <div class="user-title">
      {$_("users.email_address")}
    </div>
    <div class="user-td">
      {user.data.email ?? "-"}
    </div>
  </div>

  <div class="user-row" id="user-groups">
    <div class="user-title">
      {$_("common.groups")}
    </div>
    <div class="user-td" style="display: block">
      {#if user.groups?.length > 0}
        {#each user.groups as group}
          <p class="mb-2" style="">{group && group.name}</p>
        {/each}
      {:else}
        -
      {/if}
    </div>
  </div>
  <div class="user-row" id="user-roles">
    <div class="user-title">
      {$_("common.roles")}
    </div>
    <div class="user-td" style="display: block">
      {#if user.roles?.length > 0}
        {#each user.roles as role}
          <p class="mb-2" style="">{role && role.name}</p>
        {/each}
      {/if}
      {#if user.data.isMaster}
        <Badge
          class={user.roles > 0 && "mt-2"}
          status="success"
          label={$_("users.master")}
        />
      {:else}
        -
      {/if}
    </div>
  </div>

  <div class="user-row" id="user-apps">
    <div class="user-title">
      {$_("common.apps")}
    </div>
    <div class="user-td" style="display: block">
      {#if user.apps?.length > 0}
        {#each user.apps as app}
          {#if app}
            <p class="mb-2" style="">
              {app.name}
              <span class="text-duisab">({app.type})</span>
            </p>
          {/if}
        {/each}
      {:else}
        -
      {/if}
    </div>
  </div>

  <div class="user-row" id="user-status" role="status">
    <div class="user-title">
      {$_("idp.status")}
    </div>
    <div class="user-td" style="display: block">
      <Flex class="align-items-center">
        <FlexItem>
          {#if user.data.status}
            <Tag status={statusMap[user.data.status]}>
              {$_(`users.sta tus.${user.data.status}`)}
            </Tag>
          {:else}
            {$_("users.status.null")}
          {/if}
        </FlexItem>

        <FlexItem style="position:relative">
          <InfoBox title={$_("common.status")}
            >{$_(`users.status.${user.data.status ?? "null"}.tooltip`)}</InfoBox
          >
        </FlexItem>
      </Flex>
    </div>
  </div>

  <div class="user-row" id="user-connection" role="status">
    <div class="user-title">
      {$_("idp.connection")}
    </div>
    <div class="user-td">
      <Flex class="align-items-center">
        <FlexItem>
          <Badge
            status={user.connection === "pending" && "error"}
            label={$_(`users.connection.${user.connection}`, {
              default: $_("users.connection.manual"),
            })}
          />
        </FlexItem>

        <FlexItem style="position:relative">
          <InfoBox title={$_("common.connection")}>
            {$_(`users.connection.${user.connection}.tooltip`, {
              default: $_("users.connection.manual.tooltip"),
            })}
          </InfoBox>
        </FlexItem>
      </Flex>
    </div>
  </div>

  <div class="user-row" id="user-modified">
    <div class="user-title">
      {$_("manual.access")}
    </div>
    <div class="user-td">
      <Tag status={user.data.blocked ? "error" : "success"}>
        {user.data.blocked ? $_(`common.deactivated`) : $_(`common.active`)}
      </Tag>
    </div>
  </div>

  <div class="user-row" id="user-modified">
    <div class="user-title">
      {$_("common.last.modified")}
    </div>
    <div class="user-td">
      {user.meta?.modifiedAt
        ? format(new Date(user.meta.modifiedAt), "dd.MM.yyyy HH:mm:ss")
        : "-"}
      {#if user.meta?.modifiedBy}
        <span class="text-light ml-2" style="font-size:14px"
          >{$_("common.by")} {user.meta.modifiedBy}</span
        >
      {/if}
    </div>
  </div>

  <div class="user-row" id="user-created">
    <div class="user-title">
      {$_("common.created")}
    </div>
    <div class="user-td">
      {user.meta?.createdAt
        ? format(new Date(user.meta.createdAt), "dd.MM.yyyy HH:mm:ss")
        : "-"}
      {#if user.meta?.createdAt}
        <span class="text-light ml-2" style="font-size:14px"
          >{$_("common.by")} {user.meta.createdBy}</span
        >
      {/if}
    </div>
  </div>

  {#if user.data?.expiration}
    <div class="user-row" id="user-created">
      <div class="user-title">
        {$_("account.expiration")}
      </div>
      <div class="user-td">
        {format(new Date(user.data.expiration), "dd.MM.yyyy HH:mm:ss")}
      </div>
    </div>
  {/if}
</div>
